import { groupInstancesBySplitRules } from './groupInstancesBySplitRules';
import { resolveInstances } from './resolveInstances';
import type {
  GroupInstancesOptions,
  InstanceGroup,
  NaturalizedInstance,
  SplitContext,
  SplitRuleSet,
} from './types';

export type SplitImageIdsBySplitRulesOptions = SplitContext &
  GroupInstancesOptions & {
    /** The rules, keyed by rule id and tried in ascending priority. */
    splitRules: SplitRuleSet;
    onMissingImageId?: (imageId: string) => void;
    /**
     * Called for each resolved instance that matches no split rule (and so
     * produces no display set), e.g. a non-image SOP. Surfaces silent drops.
     */
    onUnmatchedInstance?: (instance: NaturalizedInstance) => void;
  };

/**
 * Primary entrypoint: splits a series represented by metadata imageIds into instance groups.
 */
export function splitImageIdsBySplitRules(
  imageIds: string[],
  options: SplitImageIdsBySplitRulesOptions
): InstanceGroup[] {
  const {
    getNaturalizedInstance,
    splitRules,
    onMissingImageId,
    onUnmatchedInstance,
    sortInstances,
    compareInstances,
  } = options;

  const instances = resolveInstances(imageIds, getNaturalizedInstance, {
    onMissing: onMissingImageId,
  });

  // No shortcut for an empty list: groupInstancesBySplitRules checks the rule
  // set first, also before the metadata loads.
  return groupInstancesBySplitRules(
    instances,
    splitRules,
    onUnmatchedInstance,
    {
      sortInstances,
      compareInstances,
    }
  );
}
